import {
  differenceInDays,
  differenceInHours,
  format,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import * as React from "react";
import { Tooltip } from "@fluentui/react-components";

function formatDateLabel(date: string | Date | undefined): { label: string; tooltip: string } {
  if (!date) {
    return { label: "--", tooltip: "" };
  }

  const now: Date = new Date(); // Current date and time
  const givenDate: Date = typeof date === "string" ? parseISO(date) : date; // Handle both string and Date types

  if (givenDate.getFullYear() <= 1970) {
    return { label: "--", tooltip: "" };
  }

  const diffInHours: number = differenceInHours(now, givenDate);
  const diffInDays: number = differenceInDays(now, givenDate);

  // Generate tooltip with full timestamp
  let tooltipPrefix: string;
  if (isToday(givenDate)) {
    tooltipPrefix = "Today";
  } else if (isYesterday(givenDate)) {
    tooltipPrefix = "Yesterday";
  } else {
    tooltipPrefix = format(givenDate, "MMM d, yyyy");
  }
  const tooltip = `${tooltipPrefix} @ ${format(givenDate, "h:mma").toLowerCase()}`;

  // Generate display label
  let label: string;
  if (diffInHours < 24) {
    if (diffInHours < 1) {
      const diffInMinutes: number = Math.max(
        1,
        Math.round((now.getTime() - givenDate.getTime()) / 60000),
      ); // Calculate minutes
      label = `${diffInMinutes}m ago`;
    } else {
      label = `${diffInHours}h ago`;
    }
  } else if (isToday(givenDate)) {
    label = `Today at ${format(givenDate, "p")}`; // 'p' is the time format, e.g., "1:45 PM"
  } else if (isYesterday(givenDate)) {
    label = `Yesterday at ${format(givenDate, "p")}`;
  } else if (diffInDays < 7) {
    label = `${format(givenDate, "EEE")} at ${format(givenDate, "p")}`; // 'EEE' gives short weekday name like "Tue"
  } else {
    label = format(givenDate, "MMM d, yyyy 'at' p"); // e.g., "Jan 4, 2023 at 1:45 PM"
  }

  return { label, tooltip };
}

export function generateDateLabel(date: string | Date | undefined): React.ReactElement {
  const { label, tooltip } = formatDateLabel(date);

  if (!tooltip) {
    return React.createElement("span", null, label);
  }

  return React.createElement(
    Tooltip,
    { content: tooltip, relationship: "description" },
    React.createElement("span", { style: { cursor: "default" } }, label)
  );
}
